import { useMemo, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useMembers } from '@/hooks/api/useMembers';
import {
  useRemoveSecurityGroupMember,
  useSecurityGroup,
  useSecurityGroupMembers,
} from '@/hooks/api/useSecurityGroups';
import { groupPoliciesPath } from '@/lib/policyPaths';
import { Badge } from '@/components/ui/badge';
import {
  buildGroupMemberRows,
  GROUP_MEMBER_SORT_FIELDS,
  sortGroupMemberRows,
  type GroupMemberSortField,
  type GroupMemberStatus,
} from './memberRows';
import { AddGroupMembersDialog } from './AddGroupMembersDialog';
import { SecurityGroupFormDialog } from './SecurityGroupFormDialog';

/**
 * Security group detail with its member list (KIR-3800, AC #3, #4, #12).
 *
 * Membership rows carry only a Descope user ID, so names and emails are joined
 * in from the organization members list.
 */
export function SecurityGroupDetail({
  organizationId,
  canEdit,
}: {
  organizationId: string;
  /** Owners and managers only (AC #17). */
  canEdit: boolean;
}): ReactElement | null {
  const { groupId } = useParams<{ groupId: string }>();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [userIdToRemove, setUserIdToRemove] = useState<string | null>(null);
  const { toast } = useToast();

  const {
    data: group,
    isLoading: isLoadingGroup,
    error: groupError,
  } = useSecurityGroup(organizationId, groupId);
  const { data: groupMembers, isLoading: isLoadingMembers } =
    useSecurityGroupMembers(organizationId, groupId);
  const { data: orgMembers } = useMembers(organizationId);
  const removeMutation = useRemoveSecurityGroupMember(organizationId);

  const [sortField, setSortField] = useState<GroupMemberSortField>(
    GROUP_MEMBER_SORT_FIELDS.NAME
  );
  const [sortAscending, setSortAscending] = useState(true);

  const handleSort = (field: GroupMemberSortField): void => {
    if (field === sortField) {
      setSortAscending(current => !current);
      return;
    }
    setSortField(field);
    setSortAscending(true);
  };

  const sortIcon = (field: GroupMemberSortField): string =>
    field !== sortField ? '' : sortAscending ? ' ↑' : ' ↓';

  const memberRows = useMemo(
    () =>
      sortGroupMemberRows(
        buildGroupMemberRows(groupMembers, orgMembers),
        sortField,
        sortAscending
      ),
    [groupMembers, orgMembers, sortField, sortAscending]
  );

  const existingUserIds = useMemo(
    () => (groupMembers ?? []).map(membership => membership.userId),
    [groupMembers]
  );

  const handleRemove = async (): Promise<void> => {
    if (!groupId || !userIdToRemove) return;
    try {
      await removeMutation.mutateAsync({ groupId, userId: userIdToRemove });
      toast({
        title: 'Success',
        description: 'User removed from the group',
        variant: 'success',
      });
      setUserIdToRemove(null);
    } catch (err) {
      toast({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to remove the user',
        variant: 'destructive',
      });
    }
  };

  if (!groupId) return null;

  if (isLoadingGroup) {
    return <p className="text-sm text-muted-foreground">Loading group...</p>;
  }

  if (groupError || !group) {
    return (
      <div className="space-y-4">
        <BackLink organizationId={organizationId} />
        <p className="text-sm text-destructive" role="alert">
          {groupError instanceof Error
            ? groupError.message
            : 'This security group could not be found.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackLink organizationId={organizationId} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{group.name}</h2>
          {group.description ? (
            <p className="text-sm text-muted-foreground">{group.description}</p>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to={groupPoliciesPath(organizationId, group.id)}>
              <Shield className="mr-2 h-4 w-4" />
              Edit Policies
            </Link>
          </Button>
          {canEdit ? (
            <>
              <Button variant="outline" onClick={() => setIsEditOpen(true)}>
                Edit group
              </Button>
              <Button onClick={() => setIsAddOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add users
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoadingMembers ? (
            <p className="p-6 text-sm text-muted-foreground">
              Loading members...
            </p>
          ) : memberRows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              No users in this group yet. Users in no group keep the
              organization&apos;s policies.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort(GROUP_MEMBER_SORT_FIELDS.NAME)}
                  >
                    Name{sortIcon(GROUP_MEMBER_SORT_FIELDS.NAME)}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort(GROUP_MEMBER_SORT_FIELDS.EMAIL)}
                  >
                    Email{sortIcon(GROUP_MEMBER_SORT_FIELDS.EMAIL)}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort(GROUP_MEMBER_SORT_FIELDS.ROLE)}
                  >
                    Role{sortIcon(GROUP_MEMBER_SORT_FIELDS.ROLE)}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort(GROUP_MEMBER_SORT_FIELDS.STATUS)}
                  >
                    Status{sortIcon(GROUP_MEMBER_SORT_FIELDS.STATUS)}
                  </TableHead>
                  {canEdit ? (
                    <TableHead className="text-right">Actions</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberRows.map(row => (
                  <TableRow key={row.userId}>
                    <TableCell className="font-medium">
                      {row.name || row.email || row.userId}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.email || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.role || '—'}
                    </TableCell>
                    <TableCell>
                      <MemberStatusBadge status={row.status} />
                    </TableCell>
                    {canEdit ? (
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUserIdToRemove(row.userId)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AddGroupMembersDialog
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        organizationId={organizationId}
        groupId={group.id}
        existingUserIds={existingUserIds}
      />

      <SecurityGroupFormDialog
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        organizationId={organizationId}
        group={group}
      />

      <ConfirmationDialog
        isOpen={userIdToRemove !== null}
        onClose={() => setUserIdToRemove(null)}
        onConfirm={handleRemove}
        title="Remove this user from the group?"
        description="They return to the organization's policies. You can add them back at any time."
        confirmLabel="Remove"
        isLoading={removeMutation.isPending}
      />
    </div>
  );
}

/**
 * Deployment status badge, matching the Users page's vocabulary so the two
 * screens cannot describe the same user differently.
 */
function MemberStatusBadge({
  status,
}: {
  status: GroupMemberStatus;
}): ReactElement {
  if (status === 'Active') return <Badge>Active</Badge>;
  if (status === 'Generated') {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/50 text-amber-600 dark:text-amber-400"
      >
        Generated
      </Badge>
    );
  }
  if (status === 'Invited') return <Badge variant="outline">Invited</Badge>;
  // The org members list has not resolved this user (not loaded, or they were
  // removed from the org) — say so rather than guessing "Invited".
  return <span className="text-muted-foreground">—</span>;
}

function BackLink({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  return (
    <Link
      to={`/${organizationId}/security-groups`}
      className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="mr-1 h-4 w-4" />
      All security groups
    </Link>
  );
}
